import { Button } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formTemplateStatusEnum, getFormTemplateStatusLabel, permissionLevelEnum } from '../../../../constants';
import { getFormTemplateStatusColor } from '../../../../constants/colors';
import {
    useCreateVersionFormTemplateMutation,
    useDeleteFormTemplateMutation,
    useDesactivateFormTemplateMutation
} from '../../../../hooks/mutations/useFormTemplateMutation';
import { useLatestFormTemplatesQuery } from '../../../../hooks/queries/useFormTemplatesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import { FormTemplate } from '../../../../services/apiSLTP/qhse/forms/formTemplates';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import StateCell from '../../../cells/StateCell';
import CreateFormModal from '../../../modals/qhse/forms/CreateFormModal';
import DuplicateFormModal from '../../../modals/qhse/forms/DuplicateFormModal';
import Icon from '../../../ui/Icon';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

const FormsTable: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const createModal = useBoolean(false);
    const duplicateFormModal = useBoolean(false);
    const { data: forms, isLoading, refetch } = useLatestFormTemplatesQuery({});
    const { mutate: createNewVersionForm } = useCreateVersionFormTemplateMutation({
        onSuccess: (data) => {
            navigate(`/qhse/forms/${data.data._id}`);
        },
    });
    const { mutate: desactivateForm } = useDesactivateFormTemplateMutation({
        onSuccess: (data) => {
            refetch();
        },
    });

    const { mutate: deleteForm } = useDeleteFormTemplateMutation({
        onSuccess: (data) => {
            refetch();
        },
    });

    const [selectedForm, setSelectedForm] = useState<FormTemplate | null>(null);

    const handleOpen = (form: FormTemplate) => navigate(`/qhse/forms/${form._id}`);
    const handleHistory = (form: FormTemplate) => navigate(`/qhse/forms/group/${form.groupId}`);
    const handleCreateNewVersion = (form: FormTemplate) => createNewVersionForm({ templateId: form._id });

    const columnDefs: ColDef<FormTemplate>[] = [
        {
            headerName: capitalizeFirstLetter(t('title')),
            field: 'title',
            initialFlex: 1,
            minWidth: 300,
            colId: 'title',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            colId: 'actions',
            sortable: false,
            filter: false,
            initialWidth: 250,
            cellRenderer: (params: ICellRendererParams<FormTemplate>) => {
                if (!params.data) return null;
                const form = params.data;
                return (
                    <IconLine>
                        <IconButton iconName="fileOpen" title={capitalizeFirstLetter(t('open'))} onClick={() => handleOpen(form)} />
                        <IconButton iconName="history" title={capitalizeFirstLetter(t('viewVersionsHistory'))} onClick={() => handleHistory(form)} />
                        <IconButton
                            iconName="contentCopy"
                            title={capitalizeFirstLetter(t('createCopy'))}
                            onClick={() => {
                                duplicateFormModal.setTrue();
                                setSelectedForm(form);
                            }}
                        />
                        {form.status !== formTemplateStatusEnum.DRAFT && (
                            <IconButton
                                iconName="postAdd"
                                title={capitalizeFirstLetter(t('newVersionForm'))}
                                onClick={() => {
                                    if (window.confirm(t('areYouSureYouWantToCreateNewVersionForThisForm'))) {
                                        handleCreateNewVersion(form);
                                    }
                                }}
                            />
                        )}

                        {form.status === formTemplateStatusEnum.PUBLISHED && (
                            <>
                                <IconButton
                                    iconName="cancel"
                                    title={capitalizeFirstLetter(t('desactivate'))}
                                    onClick={() => {
                                        if (window.confirm(t('areYouSureYouWantToDesactivateThisForm'))) {
                                            desactivateForm({ templateId: form._id });
                                        }
                                    }}
                                />
                            </>
                        )}
                        {form.status === formTemplateStatusEnum.DRAFT && (
                            <>
                                <IconButton
                                    iconName="deleteForever"
                                    color="error"
                                    title={capitalizeFirstLetter(t('delete'))}
                                    onClick={() => {
                                        if (window.confirm(t('areYouSureYouWantToDeleteThisForm'))) {
                                            deleteForm({ templateId: form._id });
                                        }
                                    }}
                                />
                            </>
                        )}
                    </IconLine>
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => t(getFormTemplateStatusLabel(params.data?.status)),
            cellRenderer: (params: ICellRendererParams) => (
                <StateCell text={t(getFormTemplateStatusLabel(params.data?.status))} stateColor={getFormTemplateStatusColor(params.data?.status)} />
            ),
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('description')),
            field: 'description',
            colId: 'description',
            initialWidth: 300,
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'type.designation',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('answers')),
            //field: 'nbAnswers',
            colId: 'answers',
        },
        {
            headerName: capitalizeFirstLetter(t('createdAt')),
            field: 'createdAt',
            valueGetter: (params) => params.data?.createdAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'createdAt',
        },
        {
            headerName: capitalizeFirstLetter(t('updatedAt')),
            field: 'updatedAt',
            valueGetter: (params) => params.data?.updatedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'updatedAt',
        },
        {
            headerName: capitalizeFirstLetter(t('publishedAt')),
            field: 'publishedAt',
            valueGetter: (params) => params.data?.publishedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'publishedAt',
        },

        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<FormTemplate>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={forms?.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')) || []}
                loading={isLoading}
                exportCsvFileName="forms"
                agGridProps={{ getRowId: ({ data }) => data._id }}
                headerRightComponent={
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Icon name="add" />}
                            onClick={() => {
                                createModal.setTrue();
                            }}
                        >
                            {t('newForm')}
                        </Button>
                        <IconButton
                            iconName="group"
                            title={capitalizeFirstLetter(t('usersAccess'))}
                            onClick={() => {
                                navigate('/qhse/forms/users-access');
                            }}
                        />
                    </>
                }
            />
            {createModal.value && <CreateFormModal open={createModal.value} handleClose={createModal.setFalse} refetch={refetch} />}
            {duplicateFormModal.value && (
                <DuplicateFormModal
                    open={duplicateFormModal.value}
                    handleClose={duplicateFormModal.setFalse}
                    refetch={refetch}
                    initialFormId={selectedForm?._id || ''}
                    initialTitle={selectedForm?.title || ''}
                />
            )}
        </>
    );
};

export default FormsTable;
